import { Player } from '@lottiefiles/react-lottie-player';
import clsx from 'clsx';
import React from 'react';

import { CellRendererProps } from '../../../DataTable.types';
import TopRightArrowIcon from '../../../../../../assets/Svg/TopRightArrowIcon';
import Tooltip from '../../../../Tooltip/Tooltip';
import CellCompanyLogo from './CellCompanyLogo';

import styles from './CompanyNameCellRenderer.module.scss';
import { hasDistinctBrand as isBrandDistinct, truncateByCharacters } from '../../../../../../utils/utils';

export interface handleRowClickProps {
    event?: React.MouseEvent | React.KeyboardEvent;
    row?: any;
    column?: any;
}

export const CompanyNameCellRenderer: React.FC<CellRendererProps> = ({ value, row, dynamicProps, column }) => {
    const { handleRowClick, isDisabled, showCompanyLogo, showBothNamesTooltip } = dynamicProps || {};

    // RECUR-21135 R8: on Leads rows (opt-in via showBothNamesTooltip), show both the brand
    // (row.displayName) and the legal name (row.orgName) on hover when they're distinct — other
    // consumers of this cell (Deals, OrgBasedUsersTable) don't pass the flag and keep the plain
    // single-value tooltip.
    const brandName = row?.displayName;
    const legalName = row?.orgName;
    const hasDistinctBrand = !!showBothNamesTooltip && !!legalName && isBrandDistinct(brandName, legalName);
    const tooltipContent = hasDistinctBrand ? (
        <div className={styles.NameTooltipContent}>
            <div>Brand: {brandName}</div>
            <div>Legal: {legalName}</div>
        </div>
    ) : (
        value || ''
    );

    // If disabled, render plain text without clickable styling
    if (isDisabled) {
        return (
            <div
                className={clsx(styles.CompanyNameContainer, styles.Disabled)}
                style={row?._isCollapsibleRow ? { marginLeft: '40px' } : {}}
            >
                {showCompanyLogo && <CellCompanyLogo row={row} name={value} />}
                <div className={styles.CompanyNameTextWrapper}>
                    <Tooltip content={tooltipContent} direction="top" usePortal fitContent>
                        <div className={styles.CompanyNameText}>
                            {value ? (row?._isCollapsibleRow ? truncateByCharacters(value, 15) : value) : '-'}
                        </div>
                    </Tooltip>
                </div>
            </div>
        );
    }

    return (
        <div
            className={clsx(styles.CompanyNameContainer, styles.IsPortfolio)}
            onClick={(e) => handleRowClick?.({ event: e, row, column })}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault(); // Prevent default action for Enter/Space
                    e.stopPropagation(); // Stop the event from bubbling up
                    // Call the row click handler with the assesseeOrgId
                    handleRowClick?.({ event: e, row, column });
                }
            }}
            role="button"
            tabIndex={0}
            style={row?._isCollapsibleRow ? { marginLeft: '40px' } : {}}
        >
            {showCompanyLogo && <CellCompanyLogo row={row} name={value} />}
            <div className={styles.CompanyNameTextWrapper}>
                <Tooltip content={tooltipContent} direction="top" usePortal fitContent>
                    <div className={styles.CompanyNameText}>
                        {value ? (row?._isCollapsibleRow ? truncateByCharacters(value, 15) : value) : '-'}
                    </div>
                </Tooltip>
                <div className={styles.CompanyNameIcon}>
                    <TopRightArrowIcon width={14} height={14} style={{ color: '#000000' }} />
                </div>
            </div>
        </div>
    );
};
